/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import classNames from 'classnames';

import BuilderHeader from 'components/Builder/DealBuilder/Header';
import BuilderLine from 'components/Builder/DealBuilder/Line';
import ScreenReaderAnnouncement from 'components/ScreenReaderAnnouncement';
import HeaderMessage from 'components/Builder/DealBuilder/utils/HeaderMessage';

import { useBuilderCollapse } from 'hooks/use-builder-collapse';
import { useWtrSelector } from 'redux/hooks';
import ExpandButton from './ExpandButton';

import styles from './Builder.scss';
import { BaseGroupItemPlaceholder } from '../types';
import {
  getBuilderCompletedByIndex,
  getBuilderGroupsLength,
  getBuilderSavingsByIndex,
} from '../selectors';

interface DealBuilderProps {
  alwaysOpen?: boolean;
  index: number;
  length?: number;
  GroupItemPlaceholder: BaseGroupItemPlaceholder;
  location?: string;
  completeMessage?: string;
  multipleMessage?: string;
  initialMessage?: string;
  completeIcon?: React.ReactElement<any>;
  defaultIcon?: React.ReactElement<any>;
  promotionId?: string;
  builderId?: string;
  variant: 'bundle' | 'meal deal' | 'offer' | 'wine';
}

const DealBuilder = ({
  alwaysOpen,
  index,
  length = 1,
  GroupItemPlaceholder,
  location,
  completeIcon,
  defaultIcon,
  initialMessage,
  multipleMessage,
  completeMessage,
  promotionId,
  variant,
  builderId,
}: DealBuilderProps) => {
  const groupsLength = useWtrSelector(state =>
    getBuilderGroupsLength(state, { promotionId, variant }),
  );
  const completed = useWtrSelector(state =>
    getBuilderCompletedByIndex(state, { promotionId, index, variant }),
  );
  const savings = useWtrSelector(state =>
    getBuilderSavingsByIndex(state, { promotionId, index, variant }),
  );
  const { collapsed, onClick } = useBuilderCollapse({
    alwaysOpen,
    completed,
  });

  const message = HeaderMessage({
    completed,
    length,
    complete: completeMessage,
    initial: initialMessage,
    multiple: multipleMessage,
  });

  if (!groupsLength) {
    return null;
  }

  return (
    <div
      className={classNames(styles.builder, { [styles.completed]: completed })}
      data-testid="deal-builder"
    >
      <div className={styles.container}>
        {completed && <ScreenReaderAnnouncement message={`${location} completed`} />}
        <BuilderHeader
          completed={completed}
          completeIcon={completeIcon}
          defaultIcon={defaultIcon}
          savings={savings}
          message={message}
        />
        <div className={classNames(styles.linesWrapper, { [styles.expanded]: !collapsed })}>
          <div className={styles.builderLines}>
            {Array.from({ length: groupsLength }).map((_e, groupIndex) => (
              <BuilderLine
                collapsed={collapsed}
                builderIndex={index}
                groupIndex={groupIndex}
                key={`builder-line-${groupIndex + 1}`}
                groupId={String(groupIndex + 1)}
                GroupItemPlaceholder={GroupItemPlaceholder}
                promotionId={promotionId}
                variant={variant}
                builderId={builderId}
              />
            ))}
          </div>
        </div>
        {completed && !alwaysOpen ? (
          <ExpandButton collapsed={collapsed} onClick={onClick} name={location} />
        ) : null}
      </div>
    </div>
  );
};

DealBuilder.displayName = 'DealBuilder';

export default DealBuilder;
